export const STOREFRONT_THEMES = [
  { id: "classic", label: "Classic" },
  { id: "modern", label: "Modern" },
  { id: "trendy", label: "Trendy" },
  { id: "perfecto", label: "Perfecto" },
  { id: "boxy", label: "Boxy" },
  { id: "medico", label: "Medico" },
  { id: "mixy", label: "Mixy" },
  { id: "various", label: "Various" },
  { id: "beast", label: "Beast" },
  { id: "locally", label: "Locally" },
  { id: "quicksto", label: "QuickSto" },
  { id: "bigshow", label: "BigShow" },
  { id: "furnis", label: "Furnis" },
  { id: "carparts", label: "CarParts" },
  { id: "cosmos", label: "Cosmos" },
  { id: "ondemand", label: "Ondemand" },
];

export function normalizeStorefrontTheme(value) {
  const t = String(value || "").trim().toLowerCase();
  if (t === "modern" || t === "v2" || t === "editorial") return "modern";
  if (t === "trendy" || t === "v3" || t === "beauty") return "trendy";
  if (t === "perfecto" || t === "v4" || t === "jewelry") return "perfecto";
  if (t === "boxy" || t === "v5" || t === "boutique") return "boxy";
  if (t === "medico" || t === "v6" || t === "wellness") return "medico";
  if (t === "mixy" || t === "v7" || t === "marketplace") return "mixy";
  if (t === "various" || t === "v8" || t === "grocery") return "various";
  if (t === "beast" || t === "v9" || t === "luxury") return "beast";
  if (t === "locally" || t === "v10" || t === "local") return "locally";
  if (t === "quicksto" || t === "v11" || t === "quick") return "quicksto";
  if (t === "bigshow" || t === "v12" || t === "big" || t === "helix") return "bigshow";
  if (t === "furnis" || t === "v13" || t === "furniture") return "furnis";
  if (t === "carparts" || t === "v14" || t === "car-parts" || t === "carpart" || t === "auto" || t === "parts") return "carparts";
  if (t === "cosmos" || t === "v15" || t === "beauty-cosmos" || t === "skincare") return "cosmos";
  if (t === "ondemand" || t === "v16" || t === "zepto" || t === "quick-commerce" || t === "qcommerce") return "ondemand";
  return "classic";
}

export function storefrontThemeId(config) {
  return normalizeStorefrontTheme(config?.storefront_theme);
}

/** Default brand colors when a merchant has not set primary/secondary. */
const THEME_BRAND = {
  ondemand: { primary: "#7C3AED", secondary: "#111827" },
  cosmos: { primary: "#2563EB", secondary: "#0F172A" },
  carparts: { primary: "#F25C05", secondary: "#1B2430" },
  furnis: { primary: "#1C1917", secondary: "#B08D57" },
  bigshow: { primary: "#111111", secondary: "#C8F04D" },
  quicksto: { primary: "#111111", secondary: "#7CB518" },
  locally: { primary: "#0F5C56", secondary: "#B8E031" },
  mixy: { primary: "#15803D", secondary: "#FFDC2E" },
  various: { primary: "#1B4332", secondary: "#C5D4C0" },
  medico: { primary: "#0F766E", secondary: "#B45309" },
  boxy: { primary: "#16352C", secondary: "#B45309" },
};

export function themeBrandDefaults(themeId) {
  return THEME_BRAND[normalizeStorefrontTheme(themeId)] || { primary: "#15803D", secondary: "#B45309" };
}

export function resolveStoreColors(config) {
  const defaults = themeBrandDefaults(storefrontThemeId(config));
  const primary = (config?.branding?.primary_color || "").trim();
  const secondary = (config?.branding?.secondary_color || "").trim();
  return {
    primary: primary || defaults.primary,
    secondary: secondary || defaults.secondary,
  };
}

export function isModernStorefront(config) {
  return storefrontThemeId(config) === "modern";
}

export function isTrendyStorefront(config) {
  return storefrontThemeId(config) === "trendy";
}

export function isPerfectoStorefront(config) {
  return storefrontThemeId(config) === "perfecto";
}

export function isBoxyStorefront(config) {
  return storefrontThemeId(config) === "boxy";
}

export function isMedicoStorefront(config) {
  return storefrontThemeId(config) === "medico";
}

export function isMixyStorefront(config) {
  return storefrontThemeId(config) === "mixy";
}

export function isVariousStorefront(config) {
  return storefrontThemeId(config) === "various";
}

export function isBeastStorefront(config) {
  return storefrontThemeId(config) === "beast";
}

export function isLocallyStorefront(config) {
  return storefrontThemeId(config) === "locally";
}

export function isQuickstoStorefront(config) {
  return storefrontThemeId(config) === "quicksto";
}

export function isBigshowStorefront(config) {
  return storefrontThemeId(config) === "bigshow";
}

export function isFurnisStorefront(config) {
  return storefrontThemeId(config) === "furnis";
}

export function isCarPartsStorefront(config) {
  return storefrontThemeId(config) === "carparts";
}

export function isCosmosStorefront(config) {
  return storefrontThemeId(config) === "cosmos";
}

export function isOndemandStorefront(config) {
  return storefrontThemeId(config) === "ondemand";
}

export function zeploFeaturesOn(config) {
  return isOndemandStorefront(config) || (storefrontThemeId(config) === "classic" && Boolean(config?.zeplo_classic_active));
}

export function storefrontCardVariant(config) {
  const id = storefrontThemeId(config);
  if (id === "perfecto") return "perfecto";
  if (id === "boxy") return "boxy";
  if (id === "medico") return "medico";
  if (id === "mixy") return "mixy";
  if (id === "various") return "various";
  if (id === "beast") return "beast";
  if (id === "locally") return "locally";
  if (id === "quicksto") return "quicksto";
  if (id === "bigshow") return "bigshow";
  if (id === "furnis") return "furnis";
  if (id === "carparts") return "carparts";
  if (id === "cosmos") return "cosmos";
  if (id === "ondemand") return "ondemand";
  return "default";
}

export function themeFallbackClass(id) {
  if (id === "modern") return "bg-gradient-to-br from-neutral-900 via-stone-600 to-amber-100";
  if (id === "trendy") return "bg-gradient-to-br from-rose-500 via-pink-200 to-white";
  if (id === "perfecto") return "bg-gradient-to-br from-amber-50 via-stone-300 to-stone-800";
  if (id === "boxy") return "bg-gradient-to-br from-[#16352C] via-[#C9A24A] to-[#F4F1EA]";
  if (id === "medico") return "bg-gradient-to-br from-[#0F766E] via-[#F5FBF7] to-[#F5F0E6]";
  if (id === "mixy") return "bg-gradient-to-br from-[#111111] via-[#FFDC2E] to-[#F5F5F5]";
  if (id === "various") return "bg-gradient-to-br from-[#1B4332] via-[#F1F2E8] to-[#E8C547]";
  if (id === "beast") return "bg-gradient-to-br from-black via-neutral-800 to-white";
  if (id === "locally") return "bg-gradient-to-br from-[#0F5C56] via-[#B8E031] to-white";
  if (id === "quicksto") return "bg-gradient-to-br from-[#111111] via-[#7CB518] to-white";
  if (id === "bigshow") return "bg-gradient-to-br from-[#111111] via-[#C8F04D] to-white";
  if (id === "furnis") return "bg-gradient-to-br from-[#1C1917] via-[#B08D57] to-[#F6EFE4]";
  if (id === "carparts") return "bg-gradient-to-br from-[#1B2430] via-[#F25C05] to-white";
  if (id === "cosmos") return "bg-gradient-to-br from-[#93C5FD] via-[#2563EB] to-white";
  if (id === "ondemand") return "bg-gradient-to-br from-[#7C3AED] via-[#F5F3FF] to-white";
  return "bg-gradient-to-br from-emerald-800 via-stone-300 to-white";
}
